"use client";

import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { HeroCtaButton } from "@/components/ui/hero-cta-button";

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;

function entranceTransition(
  reducedMotion: boolean | null,
  delay = 0,
  duration = 0.55,
) {
  if (reducedMotion) return { duration: 0 };
  return { delay, duration, ease: SEQUENCE_EASE };
}

export function OffersHesitationSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const reducedMotion = useReducedMotion();

  return (
    <section
      ref={sectionRef}
      data-scroll-center
      className="relative overflow-hidden bg-[#f3f3f3] px-6 py-14 sm:py-16"
    >
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-1/2 h-64 -translate-y-1/2 bg-[radial-gradient(ellipse_60%_80%_at_50%_50%,rgba(16,185,129,0.06),transparent_70%)]"
        initial={false}
        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
        transition={entranceTransition(reducedMotion, 0, 0.8)}
      />
      <motion.div
        className="relative mx-auto flex max-w-3xl flex-col items-center gap-5 rounded-[1.75rem] border border-zinc-200/80 bg-white/70 px-6 py-10 text-center shadow-[0_20px_60px_-28px_rgba(15,23,42,0.12),inset_0_1px_0_rgba(255,255,255,0.9)] backdrop-blur-sm sm:gap-6 sm:px-10 sm:py-12"
        initial={false}
        animate={
          isInView
            ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
            : { opacity: 0, y: 24, scale: 0.98, filter: "blur(10px)" }
        }
        transition={entranceTransition(reducedMotion, 0, 0.68)}
      >
        <motion.h2
          className="text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] text-[#1C1C1C] sm:text-3xl"
          initial={false}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
          transition={entranceTransition(reducedMotion, 0.1)}
        >
          Vous hésitez entre les deux ?
        </motion.h2>

        <div className="max-w-2xl space-y-3 text-pretty text-sm font-medium leading-relaxed text-zinc-500 sm:text-base">
          <motion.p
            initial={false}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            transition={entranceTransition(reducedMotion, 0.18)}
          >
            WebEssentiel convient si vous voulez un site professionnel simple. WebEvolution
            est fait pour ceux qui veulent que leur site génère réellement des clients,
            avec un accompagnement dans la durée.
          </motion.p>
          <motion.p
            className="mx-auto max-w-sm font-bold text-[#1C1C1C] sm:max-w-md"
            initial={false}
            animate={
              isInView
                ? { opacity: 1, y: 0, filter: "blur(0px)" }
                : { opacity: 0, y: 10, filter: "blur(12px)" }
            }
            transition={entranceTransition(reducedMotion, 0.28, 0.62)}
          >
            Dans le doute, réservez un audit gratuit et on vous oriente vers la bonne
            formule selon votre projet.
          </motion.p>
        </div>

        <motion.div
          initial={false}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 16, scale: 0.96 }}
          transition={entranceTransition(reducedMotion, 0.38, 0.58)}
        >
          <HeroCtaButton>Réserver mon audit gratuit</HeroCtaButton>
        </motion.div>
      </motion.div>
    </section>
  );
}
